import {
  Markup as ViewerMarkup,
  CloudMarkup as Web3DCloudMarkup,
  FreelineMarkup as Web3DFreelineMarkup,
  LineMarkup as Web3DLineMarkup,
  TextMarkup as Web3DTextMarkup,
} from '@technology/web3d-plugin-markup';
import { PanoramaControls, Tool, Web3DViewer } from '@technology/web3d';
import { PlanarControls } from '@technology/web3d-plugin-planar';
import { Web3DViewerWrapper } from '@trimble/web3d-utils';
import { Setter } from 'solid-js';
import { Markup } from './markup';
import { addMarkupToViewer } from './markup-helpers';
import { pruneViewerMarkupRefs } from '../base/shared-viewer-lifecycle';
import { hasCanonicalAnnotationOnViewer } from '../base/viewer-annotation-identity';

/**
 * Checks if a markup's geometry is fully populated.
 * Multi-tap tools fire markupmodified from their constructor (via `set color`)
 * before picks are set.
 */
export function isMarkupGeometryComplete(markup: ViewerMarkup): boolean {
  if (markup instanceof Web3DCloudMarkup) {
    return (
      typeof markup.width === 'number' &&
      markup.width > 0 &&
      typeof markup.height === 'number' &&
      markup.height > 0
    );
  }
  if (markup instanceof Web3DFreelineMarkup) {
    return Array.isArray(markup.points) && markup.points.length > 0;
  }
  if (markup instanceof Web3DTextMarkup) {
    return !!(markup.startPick?.point && markup.endPick?.point);
  }
  if (markup instanceof Web3DLineMarkup) {
    return !!(markup.startPick?.point && markup.endPick?.point);
  }
  return true;
}

/**
 * Returns true if the viewer is in an image-based navigation mode
 * (panorama or planar). Edits from such viewers should not be synced
 * because cursor intersections yield image-space positions, not world coordinates.
 */
export function isViewerInImageMode(viewer: Web3DViewer): boolean {
  const panoramaControls = viewer.tools.get(
    PanoramaControls as unknown as new (...args: unknown[]) => Tool
  );
  const planarControls = viewer.tools.get(
    PlanarControls as unknown as new (...args: unknown[]) => Tool
  );
  return panoramaControls?.enabled === true || planarControls?.enabled === true;
}

/**
 * Creates a viewer markup on each target viewer and pushes it into
 * the canonical item's viewerMarkups array.
 */
export function createMarkupOnViewers(
  markup: Markup,
  targetViewerWrappers: Web3DViewerWrapper[],
  replicationOrigin: symbol
): void {
  for (const vw of targetViewerWrappers) {
    try {
      const viewerMarkup = addMarkupToViewer(
        markup,
        vw.viewer,
        replicationOrigin
      );
      if (viewerMarkup) {
        markup.viewerMarkups.push(viewerMarkup);
      }
    } catch (error) {
      console.error(
        `Failed to replicate ${markup.type} markup ${markup.id}:`,
        error
      );
    }
  }
}

export function pruneMarkupsViewerRefs(
  removedViewerWrapper: Web3DViewerWrapper,
  setItems: Setter<Markup[]>
): void {
  pruneViewerMarkupRefs<Markup, ViewerMarkup>(
    removedViewerWrapper,
    setItems,
    (item) => item.viewerMarkups,
    (item, viewerMarkups) => ({ ...item, viewerMarkups })
  );
}

/**
 * Projects canonical markups onto a pane: creates missing annotations,
 * skips ones already stamped on the viewer. Safe to call repeatedly.
 */
export function projectMarkupsOntoViewer(
  getItems: () => Markup[],
  viewerWrapper: Web3DViewerWrapper,
  replicationOrigin: symbol,
  setItems: Setter<Markup[]>
): void {
  const items = getItems();
  if (items.length === 0) {
    return;
  }

  let projected = false;
  for (const item of items) {
    if (hasCanonicalAnnotationOnViewer(viewerWrapper.viewer, item.id)) {
      continue;
    }
    createMarkupOnViewers(item, [viewerWrapper], replicationOrigin);
    projected = true;
  }

  if (projected) {
    setItems([...getItems()]);
  }
}
